import { useState } from "react";
import { theme, FONT_RUNE } from "./theme";
import { useGame } from "./store";
import { Instructions } from "./Instructions";
import { AccountChip } from "./account/AccountChip";
import type { Capture, Piece } from "../engine";

function sumCaptured(pieces: Piece[]): number {
  return pieces.reduce((acc, p) => acc + p.value, 0);
}

const SHAPE_GLYPH: Record<Piece["shape"], string> = {
  round: "●",
  triangle: "▲",
  square: "■",
  pyramid: "◆",
};

export function TopHud({ onExit, onResign }: { onExit: () => void; onResign?: () => void }) {
  const game = useGame((s) => s.game);
  const mode = useGame((s) => s.mode);
  const humanSide = useGame((s) => s.humanSide);
  const hintsEnabled = useGame((s) => s.hintsEnabled);
  const toggleHints = useGame((s) => s.toggleHints);
  const undoLastTurn = useGame((s) => s.undoLastTurn);
  const online = useGame((s) => s.online);
  const history = useGame((s) => s.history);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const turnLabel = game.toMove === "white" ? "White" : "Black";
  const youText =
    mode === "bot"
      ? humanSide === game.toMove
        ? "(your turn)"
        : "(thinking…)"
      : "";

  // In bot mode each click undoes back to the human's turn, so the button is only meaningful
  // when at least one human-turn state exists in history.
  // Undo is only meaningful in bot mode — local hotseat, tutorial, and online all have their
  // own reasons not to allow stepping back from the HUD.
  const hasMeaningfulUndo =
    mode === "bot" && history.some((s) => s.toMove === humanSide);
  const undoDisabled = mode !== "bot" || !hasMeaningfulUndo || !!online;
  const undoTitle = online
    ? "Undo is unavailable in online games"
    : mode === "tutorial"
      ? "Undo is unavailable in the tutorial"
      : mode === "local"
        ? "Undo is unavailable in local hotseat"
        : !hasMeaningfulUndo
          ? "Nothing to undo yet"
          : "Step back one turn";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
        padding: "calc(8px + var(--safe-top)) calc(14px + var(--safe-right)) 8px calc(14px + var(--safe-left))",
        background: theme.bgElev,
        borderBottom: `1px solid ${theme.boardEdge}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div className="rune" style={{ fontSize: 18, color: theme.parchment, letterSpacing: 1 }}>
          {turnLabel} <span style={{ color: theme.accentSoft, marginLeft: 6, fontSize: 13 }}>{youText}</span>
        </div>
        <button
          onClick={undoLastTurn}
          disabled={undoDisabled}
          style={{
            ...headerButton,
            cursor: undoDisabled ? "not-allowed" : "pointer",
            opacity: undoDisabled ? 0.4 : 1,
          }}
          title={undoTitle}
        >
          ↶ Undo
        </button>
        {onResign && (
          <button onClick={onResign} style={headerButton}>
            Resign
          </button>
        )}
        <button onClick={onExit} style={headerButton}>
          Menu
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {mode === "bot" && (
          <button
            onClick={toggleHints}
            style={{
              ...hintsToggle,
              background: hintsEnabled ? theme.accent : theme.boardEdge,
              color: hintsEnabled ? theme.bg : theme.parchment,
              borderColor: hintsEnabled ? theme.accent : theme.inkSoft,
            }}
            aria-pressed={hintsEnabled}
            title={hintsEnabled ? "Hints on — click to turn off" : "Hints off — click to turn on"}
          >
            Hints {hintsEnabled ? "on" : "off"}
          </button>
        )}
        <button
          onClick={() => setInstructionsOpen(true)}
          style={helpButton}
          aria-label="How to play"
          title="How to play"
        >
          ?
        </button>
        <div style={{ fontSize: 12, color: theme.boardLabel }} className="rune">
          {game.victoryConfig.level.toUpperCase()}
          {game.victoryConfig.level === "common" || game.victoryConfig.level === "proper"
            ? ` · ${game.victoryConfig.commonThreshold}`
            : ""}
        </div>
        <AccountChip compact />
      </div>
      <Instructions open={instructionsOpen} onClose={() => setInstructionsOpen(false)} />
    </div>
  );
}

const helpButton: React.CSSProperties = {
  width: 40,
  height: 40,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT_RUNE,
  fontSize: 16,
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 999,
  cursor: "pointer",
};

const hintsToggle: React.CSSProperties = {
  height: 40,
  padding: "0 14px",
  display: "inline-flex",
  alignItems: "center",
  fontFamily: FONT_RUNE,
  fontSize: 12,
  letterSpacing: 1,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 999,
  cursor: "pointer",
};

const headerButton: React.CSSProperties = {
  fontFamily: FONT_RUNE,
  fontSize: 12,
  letterSpacing: 1,
  height: 36,
  padding: "0 12px",
  background: theme.boardEdge,
  color: theme.parchment,
  border: `1px solid ${theme.inkSoft}`,
  borderRadius: 4,
  cursor: "pointer",
};

export function BottomHud() {
  const captured = useGame((s) => s.game.captured);
  const lastCaptures = useGame((s) => s.lastCaptures);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding:
          "8px calc(12px + var(--safe-right)) calc(8px + var(--safe-bottom)) calc(12px + var(--safe-left))",
        background: theme.bgElev,
        borderTop: `1px solid ${theme.boardEdge}`,
      }}
    >
      <CapturedRow label="White's captures" pieces={captured.white} totalColor={theme.accentSoft} />
      <CapturedRow label="Black's captures" pieces={captured.black} totalColor={theme.accentSoft} />
      <div style={{ display: "flex", marginTop: 4 }}>
        <LastCaptureLabel captures={lastCaptures} />
      </div>
    </div>
  );
}

function CapturedRow({ label, pieces, totalColor }: { label: string; pieces: Piece[]; totalColor: string }) {
  const total = sumCaptured(pieces);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: theme.parchment }}>
      <span className="rune" style={{ minWidth: 130, color: theme.boardLabel }}>{label}</span>
      <span style={{ fontFamily: FONT_RUNE, color: totalColor, minWidth: 32 }}>{total}</span>
      <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {pieces.slice(-12).map((p, i) => (
          <span
            key={i}
            title={`${p.shape} ${p.value}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              fontFamily: FONT_RUNE,
              color: theme.parchment,
              opacity: 0.85,
            }}
          >
            <span style={{ color: theme.boardLabel }}>{SHAPE_GLYPH[p.shape]}</span>
            <span>{p.value}</span>
          </span>
        ))}
      </span>
    </div>
  );
}

function LastCaptureLabel({ captures }: { captures: Capture[] }) {
  if (!captures.length) return <span style={{ color: theme.boardLabel, fontSize: 13 }}>—</span>;
  return (
    <span style={{ color: theme.accentSoft, fontSize: 12 }} className="rune">
      {captures.map((c) => `${c.method} · ${c.formula}`).join(" / ")}
    </span>
  );
}
